import { tokenStore } from './tokens'
import type { TokenPair } from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>
  /** Seconds from the Retry-After header (429 / 503), when present. */
  readonly retryAfterSec?: number

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>, retryAfterSec?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.retryAfterSec = retryAfterSec
  }

  /** Rate limited by the server (429 resource_exhausted; a full queue is 429 queue_full, not a rate limit). */
  get rateLimited(): boolean {
    return this.code === 'resource_exhausted'
  }
}

/** Parses a Retry-After header (delta-seconds or HTTP date) into whole seconds. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | undefined {
  if (!value) return undefined
  const v = value.trim()
  if (/^\d+$/.test(v)) return Number(v)
  const at = Date.parse(v)
  if (Number.isNaN(at)) return undefined
  return Math.max(0, Math.ceil((at - now) / 1000))
}

type Query = Record<string, string | number | boolean | undefined | null>

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** multipart/form-data body (file uploads); the browser sets the boundary, so no Content-Type here. */
  formData?: FormData
  query?: Query
  /** Skip auth header and refresh handling (login/refresh endpoints). */
  anonymous?: boolean
  signal?: AbortSignal
}

const BASE = '/api/v1'

let onUnauthorized: () => void = () => {}

/** Registers the callback run when the session cannot be refreshed. */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn
}

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function parseError(res: Response): Promise<ApiError> {
  const retry = parseRetryAfter(res.headers.get('Retry-After'))
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string; details?: Record<string, unknown> } }
    if (body.error) {
      return new ApiError(res.status, body.error.code ?? 'unknown', body.error.message ?? res.statusText, body.error.details, retry)
    }
  } catch {
    // non-JSON error body
  }
  return new ApiError(res.status, res.status === 429 ? 'resource_exhausted' : 'http_error', res.statusText || `HTTP ${res.status}`, undefined, retry)
}

async function send(path: string, opts: RequestOptions, accessToken?: string): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined && !opts.formData) headers['Content-Type'] = 'application/json'
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.formData ?? (opts.body === undefined ? undefined : JSON.stringify(opts.body)),
    signal: opts.signal,
  })
}

let refreshing: Promise<boolean> | null = null

/** Refreshes tokens once even when many requests fail concurrently. */
async function refreshTokens(): Promise<boolean> {
  if (refreshing) return refreshing
  const stored = tokenStore.get()
  if (!stored) return false
  refreshing = (async () => {
    try {
      const res = await send('/auth/refresh', { method: 'POST', body: { refresh_token: stored.refreshToken }, anonymous: true })
      if (!res.ok) return false
      tokenStore.set((await res.json()) as TokenPair)
      return true
    } catch {
      return false
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

/** Calls the REST v1 API and returns the decoded JSON body. */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await authed(path, opts)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Calls the REST v1 API and returns the body as text (CSV exports). */
export async function apiText(path: string, opts: RequestOptions = {}): Promise<string> {
  return (await authed(path, opts)).text()
}

async function authed(path: string, opts: RequestOptions): Promise<Response> {
  let res = await send(path, opts, opts.anonymous ? undefined : tokenStore.get()?.accessToken)
  if (res.status === 401 && !opts.anonymous) {
    if (await refreshTokens()) {
      res = await send(path, opts, tokenStore.get()?.accessToken)
    }
    if (res.status === 401) {
      tokenStore.clear()
      onUnauthorized()
    }
  }
  if (!res.ok) throw await parseError(res)
  return res
}

/** Human-readable message for any thrown value. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError && err.rateLimited) {
    return err.retryAfterSec !== undefined ? `Quá nhiều yêu cầu, thử lại sau ${Math.max(1, err.retryAfterSec)} giây` : 'Quá nhiều yêu cầu, thử lại sau ít phút'
  }
  if (err instanceof ApiError && err.code === 'queue_full') return `Queue đã đầy, không nhận thêm job: ${err.message}`
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Đã có lỗi xảy ra'
}
